import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TableHeader, TableRow, TableHead, TableBody, TableCell, Table } from "@/components/ui/table";
import { convertGradeLevelToString } from "@/lib/string-utiils";
import {
  EnrollmentClassDayStatus,
  EnrollmentStatus,
  type ParentEnrollmentClassDTO,
  type ParentSemesterParticipationAgreementDTO,
} from "@/orval/model";
import { useParentEnrollmentGetEnrollmentStudents } from "@/orval/parent-enrollment";
import { useParentParticipationAgreementGetOutstandingParticipationAgreements } from "@/orval/parent-participation-agreement";
import { ParticipationAgreementModal } from "@/components/participation-agreement-modal";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parse, parseISO } from "date-fns";
import { InfoIcon, CalendarClock, FileText } from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { Form } from "@/components/form/form";
import { OrganizationSemestersComboboxFormField } from "@/components/form/form-field/combobox/organization-semesters-combobox-form-field";
import { PortalPageHeader } from "@/components/portal-page-header";

import { enrollmentSearchSchema, useEnrollmentSemester } from "./-components/use-enrollment-semester";

export const Route = createFileRoute("/_app/organizations/$organizationId/parent/enrollment")({
  validateSearch: enrollmentSearchSchema,
  component: RouteComponent,
});

const timeParseReferenceDate = new Date(0);

const getStatusBadges = (
  status: EnrollmentClassDayStatus,
  classes: ParentEnrollmentClassDTO[],
  enrollmentOpenDate: string | null | undefined,
) => {
  const enrolledClassesCount = classes.filter((classItem) => classItem.status === EnrollmentStatus.Enrolled).length;
  const waitllistedClassesCount = classes.filter(
    (classItem) => classItem.status === EnrollmentStatus.Waitlisted,
  ).length;

  switch (status) {
    case EnrollmentClassDayStatus.Enrolled:
      return (
        <div className="flex flex-col gap-1">
          {!!enrolledClassesCount && (
            <Badge className="bg-green-600 dark:bg-green-900/60 dark:text-green-300 whitespace-nowrap hover:bg-green-600 dark:hover:bg-green-900/60 text-center flex items-center justify-center">
              Enrolled ({enrolledClassesCount})
            </Badge>
          )}
          {!!waitllistedClassesCount && (
            <Badge className="bg-transparent border-amber-500 text-amber-500 dark:border-amber-600 dark:text-amber-400 whitespace-nowrap hover:bg-transparent text-center flex items-center justify-center">
              Waitlisted ({waitllistedClassesCount})
            </Badge>
          )}
        </div>
      );
    case EnrollmentClassDayStatus.OpensSoon:
      return (
        <Badge variant="outline" className="text-amber-500 border-amber-500 whitespace-nowrap">
          Opens {enrollmentOpenDate ? format(parseISO(enrollmentOpenDate), "MMM d") : "soon"}
        </Badge>
      );
    case EnrollmentClassDayStatus.ReadyToEnroll:
      return (
        <Badge variant="secondary" className="whitespace-nowrap">
          Ready to Enroll
        </Badge>
      );
    case EnrollmentClassDayStatus.OutstandingParticipationAgreements:
      return (
        <Badge variant="outline" className="text-accent border-accent whitespace-nowrap flex items-center gap-1">
          <FileText className="size-3" />
          Agreement Required
        </Badge>
      );
    case EnrollmentClassDayStatus.Closed:
      return (
        <div className="flex flex-col gap-1">
          {!!enrolledClassesCount && (
            <Badge className="bg-green-600 dark:bg-green-900/60 dark:text-green-300 whitespace-nowrap hover:bg-green-600 dark:hover:bg-green-900/60 text-center flex items-center justify-center">
              Enrolled ({enrolledClassesCount})
            </Badge>
          )}
          {!!waitllistedClassesCount && (
            <Badge className="bg-transparent border-amber-500 text-amber-500 dark:border-amber-600 dark:text-amber-400 whitespace-nowrap hover:bg-transparent text-center flex items-center justify-center">
              Waitlisted ({waitllistedClassesCount})
            </Badge>
          )}
          {!enrolledClassesCount && !waitllistedClassesCount && (
            <Badge variant="outline" className="whitespace-nowrap flex items-center justify-center">
              Closed
            </Badge>
          )}
        </div>
      );
    default:
      return null;
  }
};

const getActionButton = (
  status: EnrollmentClassDayStatus,
  studentId: number,
  classDayId: number,
  selectedSemesterId: number | undefined,
  onViewAgreements?: () => void,
) => {
  switch (status) {
    case EnrollmentClassDayStatus.Enrolled:
      return (
        <Button asChild variant="outline" size="sm" className="w-full md:w-auto">
          <Link
            from={Route.fullPath}
            to="/organizations/$organizationId/parent/enrollment/$studentId/$classDayId"
            params={{ studentId, classDayId }}
            search={{ semesterId: selectedSemesterId }}
          >
            Modify Enrollment
          </Link>
        </Button>
      );
    case EnrollmentClassDayStatus.OpensSoon:
      return (
        <Button asChild variant="outline" size="sm" className="w-full md:w-auto">
          <Link
            from={Route.fullPath}
            to="/organizations/$organizationId/parent/enrollment/$studentId/$classDayId"
            params={{ studentId, classDayId }}
            search={{ semesterId: selectedSemesterId }}
          >
            Preview Enrollment
          </Link>
        </Button>
      );
    case EnrollmentClassDayStatus.ReadyToEnroll:
      return (
        <Button asChild size="sm" className="w-full md:w-auto">
          <Link
            from={Route.fullPath}
            to="/organizations/$organizationId/parent/enrollment/$studentId/$classDayId"
            params={{ studentId, classDayId }}
            search={{ semesterId: selectedSemesterId }}
          >
            Enroll Now
          </Link>
        </Button>
      );
    case EnrollmentClassDayStatus.OutstandingParticipationAgreements:
      return (
        <Button
          onClick={onViewAgreements}
          size="sm"
          variant="accent"
          className="w-full md:w-auto flex items-center gap-2"
        >
          <FileText className="size-4" />
          View Agreements
        </Button>
      );
    default:
      return null;
  }
};

const ClassEnrollmentSummary = ({ classItem }: { classItem: ParentEnrollmentClassDTO }) => (
  <div className="bg-muted/30 p-3 rounded-md">
    <div className="flex items-center gap-3">
      {classItem.imageUrl && (
        <img
          src={classItem.imageUrl}
          alt={`${classItem.className} class`}
          className="size-14 shrink-0 rounded-md border object-cover bg-background"
          loading="lazy"
        />
      )}
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="font-medium flex gap-2 items-center">
          <span className="truncate">{classItem.className}</span>
          {classItem.status === EnrollmentStatus.Waitlisted && (
            <Badge variant="outline" className="text-amber-500 border-amber-500 text-xs bg-transparent shrink-0">
              Waitlisted
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground flex items-center gap-1">
          <CalendarClock className="size-3.5 shrink-0" />
          {format(parse(classItem.startTime, "HH:mm:ss", timeParseReferenceDate), "h:mm a")} -{" "}
          {format(parse(classItem.endTime, "HH:mm:ss", timeParseReferenceDate), "h:mm a")}
        </div>
      </div>
    </div>
  </div>
);

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { semesterId, selectedSemesterId } = useEnrollmentSemester(organizationId, search.semesterId);
  const values = useMemo(() => ({ semesterId: search.semesterId ?? semesterId }), [search.semesterId, semesterId]);

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-4 print:mb-2"
        title="Enrollment Dashboard"
        subtitle={<span className="print:text-black">Manage your children's class enrollments</span>}
      />

      <Form<{ semesterId?: number }>
        onChange={(data) => {
          if (data.semesterId !== values.semesterId) {
            void navigate({ search: { semesterId: data.semesterId } });
          }
        }}
        useFormProps={{ values }}
      >
        <OrganizationSemestersComboboxFormField
          organizationId={organizationId}
          name="semesterId"
          className="md:w-64 mb-4"
        />
      </Form>

      <EnrollmentBody
        key={`${organizationId}-${semesterId}`}
        semesterId={semesterId}
        selectedSemesterId={selectedSemesterId}
        organizationId={organizationId}
      />
    </div>
  );
}

interface EnrollmentBodyProps {
  semesterId?: number;
  selectedSemesterId?: number;
  organizationId: number;
}

const EnrollmentBody: FC<EnrollmentBodyProps> = ({ semesterId, selectedSemesterId, organizationId }) => {
  const [participationAgreementModalOpen, setParticipationAgreementModalOpen] = useState(false);
  const [currentAgreement, setCurrentAgreement] = useState<ParentSemesterParticipationAgreementDTO | null>(null);

  const { data: enrollmentStudents } = useParentEnrollmentGetEnrollmentStudents(organizationId, semesterId!, {
    query: {
      enabled: !!semesterId,
    },
  });

  const { data: participationAgreements, refetch: refetchAgreements } =
    useParentParticipationAgreementGetOutstandingParticipationAgreements(organizationId, semesterId!, {
      query: {
        enabled: !!semesterId,
      },
    });

  const handleViewAgreements = () => {
    if (participationAgreements && participationAgreements.length > 0) {
      setCurrentAgreement(participationAgreements[0]);
      setParticipationAgreementModalOpen(true);
    }
  };

  const handleAgreementAcknowledged = async () => {
    // Refetch agreements to get updated list
    const { data: updatedAgreements } = await refetchAgreements();

    // If there are more agreements, show the next one
    if (updatedAgreements && updatedAgreements.length > 0) {
      setTimeout(() => {
        setCurrentAgreement(updatedAgreements[0]);
        setParticipationAgreementModalOpen(true);
      }, 500);
    } else {
      // No more agreements, close modal
      setCurrentAgreement(null);
      setParticipationAgreementModalOpen(false);
    }
  };

  if (!semesterId) {
    return (
      <Alert>
        <InfoIcon className="size-4" />
        <AlertTitle>Select a semester</AlertTitle>
        <AlertDescription>Please select a semester to view your children's enrollments.</AlertDescription>
      </Alert>
    );
  }

  if (!enrollmentStudents) {
    return (
      <div className="flex flex-col gap-y-6">
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-muted/50 p-4">
            <div className="flex justify-between">
              <div>
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-24 mt-2" />
              </div>
              <Skeleton className="h-8 w-24" />
            </div>
          </div>
          <div className="p-4">
            <Skeleton className="h-64" />
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="bg-muted/50 p-4">
            <div className="flex justify-between">
              <div>
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-24 mt-2" />
              </div>
              <Skeleton className="h-8 w-24" />
            </div>
          </div>
          <div className="p-4">
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (enrollmentStudents.length === 0) {
    return (
      <Alert>
        <InfoIcon className="size-4" />
        <AlertTitle>No enrolled classes</AlertTitle>
        <AlertDescription>
          None of your students are currently enrolled in any classes for this semester.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-6">
      {enrollmentStudents.map((student) => (
        <Card key={student.studentId}>
          <CardHeader className="bg-muted/50 pb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 w-full">
              <div>
                <CardTitle>{student.studentName}</CardTitle>
                <CardDescription>{convertGradeLevelToString(student.gradeLevel)}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Mobile cards */}
            <div className="flex flex-col divide-y md:hidden gap-y-2">
              {student.classDays.map((classDay) => (
                <div key={classDay.classDayName} className="p-4">
                  <div className="flex flex-col gap-2">
                    <div className="font-medium">{classDay.classDayName}</div>
                    <div className="w-min">
                      {getStatusBadges(classDay.status, classDay.classes, classDay.enrollmentOpenDate)}
                    </div>
                    <div>
                      {classDay.classes.length > 0 ? (
                        <div className="flex flex-col gap-y-3">
                          {classDay.classes
                            .sort((x, y) => x.status - y.status)
                            .map((classItem) => (
                              <ClassEnrollmentSummary key={classItem.classId} classItem={classItem} />
                            ))}
                        </div>
                      ) : (
                        <div className="text-muted-foreground">
                          <EmptyEnrollmentDay
                            status={classDay.status}
                            enrollmentOpenDate={classDay.enrollmentOpenDate}
                          />
                        </div>
                      )}
                    </div>
                    <div className="w-full">
                      {getActionButton(
                        classDay.status,
                        student.studentId,
                        classDay.classDayId,
                        selectedSemesterId,
                        handleViewAgreements,
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Table for md+ */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-inherit">
                    <TableHead className="w-[120px]">Day</TableHead>
                    <TableHead className="w-[150px]">Status</TableHead>
                    <TableHead>Classes</TableHead>
                    <TableHead className="w-[150px] text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {student.classDays.map((classDay) => (
                    <TableRow key={classDay.classDayName} className="hover:bg-inherit">
                      <TableCell className="font-medium align-middle">{classDay.classDayName}</TableCell>
                      <TableCell className="align-middle">
                        {getStatusBadges(classDay.status, classDay.classes, classDay.enrollmentOpenDate)}
                      </TableCell>
                      <TableCell>
                        {classDay.classes.length > 0 ? (
                          <div className="flex flex-col gap-y-3">
                            {classDay.classes
                              .sort((x, y) => x.status - y.status)
                              .map((classItem) => (
                                <ClassEnrollmentSummary key={classItem.classId} classItem={classItem} />
                              ))}
                          </div>
                        ) : (
                          <div className="text-muted-foreground">
                            <EmptyEnrollmentDay
                              status={classDay.status}
                              enrollmentOpenDate={classDay.enrollmentOpenDate}
                            />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right align-middle">
                        {getActionButton(
                          classDay.status,
                          student.studentId,
                          classDay.classDayId,
                          selectedSemesterId,
                          handleViewAgreements,
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ))}

      <ParticipationAgreementModal
        isOpen={participationAgreementModalOpen}
        onClose={() => setParticipationAgreementModalOpen(false)}
        agreement={currentAgreement}
        organizationId={organizationId}
        semesterId={semesterId!}
        onAgreementAcknowledged={handleAgreementAcknowledged}
      />
    </div>
  );
};

function EmptyEnrollmentDay({
  status,
  enrollmentOpenDate,
}: {
  status: EnrollmentClassDayStatus;
  enrollmentOpenDate?: string | null;
}) {
  if (status !== EnrollmentClassDayStatus.OpensSoon) {
    return <p>No classes enrolled</p>;
  }
  if (!enrollmentOpenDate) {
    return null;
  }
  return <p>Available for enrollment on {format(parseISO(enrollmentOpenDate), "MMM do 'at' h:mm a")}</p>;
}
